import React, {useEffect, useLayoutEffect, useState} from "react";
import {createTheme, responsiveFontSizes, ThemeProvider as MuiThemeProvider, useTheme,} from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {createLocalStorageStateHook} from "use-local-storage-state";

// One dark, warm palette for the whole site; the light/dark toggle is deprecated, so both names map to it
const palette = {
    type: "dark",
    primary: {
        main: "#EC7A96",
        light: "#F5A5B9",
        dark: "#C95571",
        contrastText: "#1B0B12",
    },
    secondary: {
        main: "#EC7A96",
        light: "#F5A5B9",
        dark: "#C95571",
        contrastText: "#1B0B12",
    },
    error: {
        main: "#F07C76",
    },
    background: {
        default: "#120B10",
        paper: "#1B1219",
    },
    text: {
        primary: "#F5ECEF",
        secondary: "rgba(245, 236, 239, 0.66)",
        disabled: "rgba(245, 236, 239, 0.38)",
    },
    divider: "rgba(255, 220, 232, 0.10)",
    action: {
        hover: "rgba(255, 220, 232, 0.06)",
        selected: "rgba(255, 220, 232, 0.10)",
    },
};

const serif = '"Lora", Georgia, "Times New Roman", serif';
const sans = '"Inter", "Helvetica Neue", Arial, sans-serif';
const raised = "#21161F";
const line = "rgba(255, 220, 232, 0.10)";

const themeConfig = {
    light: {palette},
    dark: {palette},

    // Values for both themes
    common: {
        shape: {
            borderRadius: 12,
        },
        typography: {
            fontSize: 15,
            fontFamily: sans,
            h1: {fontFamily: serif, fontWeight: 600, letterSpacing: "-0.02em"},
            h2: {fontFamily: serif, fontWeight: 600, letterSpacing: "-0.015em"},
            h3: {fontFamily: serif, fontWeight: 600, letterSpacing: "-0.01em"},
            h4: {fontFamily: serif, fontWeight: 600},
            h5: {fontFamily: serif, fontWeight: 600},
            h6: {fontWeight: 600, letterSpacing: "-0.005em"},
            button: {textTransform: "none", fontWeight: 600, letterSpacing: 0},
            overline: {fontWeight: 600, letterSpacing: "0.12em"},
        },
        breakpoints: {
            values: {
                xs: 0,
                sm: 600,
                md: 960,
                lg: 1200,
                xl: 1920,
            },
        },
        props: {
            MuiButton: {disableElevation: true},
            MuiButtonBase: {disableRipple: false},
        },
        // Override component styles
        overrides: {
            // Global styles
            MuiCssBaseline: {
                "@global": {
                    html: {
                        colorScheme: "dark",
                        WebkitTextSizeAdjust: "100%",
                        // keep the scrollbar's space on short pages too, so the page doesn't shift sideways between pages
                        scrollbarGutter: "stable",
                    },
                    body: {
                        backgroundColor: palette.background.default,
                        backgroundImage: "radial-gradient(1200px 600px at 50% -200px, rgba(236, 122, 150, 0.10), transparent 70%)",
                        backgroundRepeat: "no-repeat",
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                    },
                    "::selection": {
                        backgroundColor: "rgba(236, 122, 150, 0.35)",
                    },
                    a: {
                        color: palette.primary.light,
                    },
                    "#__next": {
                        // Flex column that is height
                        // of viewport so that footer
                        // can push self to bottom by
                        // with auto margin-top
                        minHeight: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        // Prevent child elements from
                        // shrinking when content
                        // is taller than the screen
                        // (quirk of flex parent)
                        "& > *": {
                            flexShrink: 0,
                        },
                    },
                },
            },
            MuiButton: {
                root: {
                    borderRadius: 999,
                    padding: "8px 18px",
                },
                sizeLarge: {
                    padding: "12px 26px",
                    fontSize: "1rem",
                },
                sizeSmall: {
                    padding: "4px 12px",
                },
                text: {
                    padding: "8px 14px",
                },
                outlined: {
                    padding: "7px 17px",
                    borderColor: "rgba(255, 220, 232, 0.22)",
                },
                containedPrimary: {
                    "&:hover": {
                        backgroundColor: palette.primary.light,
                    },
                },
            },
            MuiIconButton: {
                root: {
                    color: palette.text.secondary,
                },
            },
            MuiPaper: {
                root: {
                    backgroundImage: "none",
                },
                rounded: {
                    borderRadius: 16,
                },
            },
            MuiCard: {
                root: {
                    border: `1px solid ${line}`,
                },
            },
            MuiDialog: {
                paper: {
                    backgroundColor: raised,
                    border: `1px solid ${line}`,
                    boxShadow: "0 40px 120px rgba(0, 0, 0, 0.6)",
                },
                paperFullScreen: {
                    border: "none",
                },
            },
            MuiDialogTitle: {
                root: {
                    padding: "24px 24px 8px",
                },
            },
            MuiDialogContent: {
                root: {
                    padding: "8px 24px",
                },
            },
            MuiDialogActions: {
                root: {
                    padding: "16px 24px 24px",
                },
            },
            MuiBackdrop: {
                root: {
                    backgroundColor: "rgba(8, 4, 7, 0.72)",
                    backdropFilter: "blur(6px)",
                },
                invisible: {
                    backdropFilter: "none",
                },
            },
            MuiPopover: {
                paper: {
                    backgroundColor: raised,
                    border: `1px solid ${line}`,
                },
            },
            MuiDrawer: {
                paper: {
                    backgroundColor: "#170F15",
                    borderLeft: `1px solid ${line}`,
                },
            },
            MuiChip: {
                root: {
                    borderRadius: 999,
                    fontWeight: 500,
                    backgroundColor: "rgba(255, 220, 232, 0.07)",
                },
                outlined: {
                    borderColor: "rgba(255, 220, 232, 0.18)",
                },
                sizeSmall: {
                    height: 24,
                    fontSize: "0.75rem",
                },
            },
            MuiOutlinedInput: {
                root: {
                    borderRadius: 14,
                    backgroundColor: "rgba(255, 220, 232, 0.03)",
                },
                notchedOutline: {
                    borderColor: "rgba(255, 220, 232, 0.16)",
                },
            },
            MuiInputBase: {
                input: {
                    "&::placeholder": {
                        opacity: 0.55,
                    },
                },
            },
            MuiSkeleton: {
                root: {
                    backgroundColor: "rgba(255, 220, 232, 0.07)",
                },
            },
            MuiLinearProgress: {
                root: {
                    borderRadius: 4,
                },
            },
            MuiTableCell: {
                root: {
                    borderBottom: `1px solid ${line}`,
                },
            },
            MuiDivider: {
                root: {
                    backgroundColor: line,
                },
            },
            MuiAppBar: {
                colorDefault: {
                    backgroundColor: palette.background.default,
                },
            },
        },
    },
};

function getTheme(name) {
    // Create MUI theme from themeConfig
    return responsiveFontSizes(createTheme({
        ...themeConfig[name],
        // Merge in common values
        ...themeConfig.common,
        overrides: {
            // Merge overrides
            ...(themeConfig[name] && themeConfig[name].overrides),
            ...(themeConfig.common && themeConfig.common.overrides),
        },
    }));
}

// Create a local storage hook for dark mode preference
const useDarkModeStorage = createLocalStorageStateHook("isDarkMode");

export const ThemeProvider = (props) => {
    // Get system dark mode preference
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

    // Get stored dark mode preference
    let [isDarkModeStored, setIsDarkModeStored] = useDarkModeStorage();

    // Only used stored preference after hydration to avoid client/server mismatch
    const hasHydrated = useHasHydrated();
    if (!hasHydrated) {
        isDarkModeStored = undefined;
    }

    // Use stored dark mode with fallback to system preference
    const isDarkMode =
        isDarkModeStored === undefined ? prefersDarkMode : isDarkModeStored;

    // Get MUI theme object
    const themeName = isDarkMode ? "dark" : "light";
    const theme = getTheme(themeName);

    // Add toggle function to theme object
    theme.palette.toggle = () => setIsDarkModeStored((value) => !value);

    // Since Next.js server-renders we need to remove
    // the server-side injected CSS on mount so the
    // client can take over with managing styles.
    useEffect(() => {
        const jssStyles = document.querySelector("#jss-server-side");
        if (jssStyles) {
            jssStyles.parentElement.removeChild(jssStyles);
        }
    }, []);

    return (
        <MuiThemeProvider theme={theme}>
            {/* Set global MUI styles */}
            <CssBaseline/>
            {props.children}
        </MuiThemeProvider>
    );
};

// Hook for detecting dark mode and toggling between light/dark
// More convenient than reading theme.palette.type from useTheme
export function useDarkMode() {
    // Get current Material UI theme
    const theme = useTheme();
    // Check if it's the dark theme
    const isDarkMode = theme.palette.type === "dark";
    // Return object containing dark mode value and toggle function
    return {value: isDarkMode, toggle: theme.palette.toggle};
}

// Hook that tells us when hydration is complete so that we can
// safely use the value returned by useDarkModeStorage without
// risking a mismatch between server and client.
// This will hopefully be built-in to the use-local-storage-state library soon
// See https://github.com/astoilkov/use-local-storage-state/issues/23
function useHasHydrated() {
    const [hasHydrated, setHasHydrated] = useState(false);

    const isServer = typeof window === "undefined";
    // To reduce flicker, we use `useLayoutEffect` so that app re-renders before
    // before React has painted to the browser.
    // React throws a warning when using useLayoutEffect on the server so
    // we use useEffect on the server (no-op) and useLayoutEffect in the browser.
    const useEffectFn = isServer ? useEffect : useLayoutEffect;

    useEffectFn(() => {
        setHasHydrated(true);
    }, []);

    return hasHydrated;
}
